/* eslint-disable max-lines */
import ContactFormFields from '@logic/contactform';
import MasterDataProvider from '@logic/masterdataprovider';
import {
  Avatar,
  Box,
  IconButton,
  TextField,
  Typography,
} from '@material-ui/core';
import CircularProgress from '@material-ui/core/CircularProgress';
import { makeStyles } from '@material-ui/core/styles';
import Autocomplete from '@material-ui/lab/Autocomplete';
import CustomAvatar from '@src/components/Common/CustomAvatar';
import StringUtils from '@utils/strings';
import { useEffect, useState } from 'react';
import { FormattedMessage, injectIntl } from 'react-intl';
import { E164PhoneField } from '../CustomMaterialUI';
import InputHelperText from './InputHelperText';
import MobileNumberField from './MobileNumberField';
import ReadOnlyContactFields from './ReadOnlyContactFields';
import EditIcon from './SvgIcons';

const {
  ID,
  FULL_NAME,
  ID_NUMBER,
  EMAIL,
  PHONE_NUMBER,
  MOBILE_NUMBER,
  ADDRESS,
  COMPANY,
  COUNTRY,
  PROVINCE,
  CITY_NAME,
} = ContactFormFields.FIELDS;

const comboBoxStyles = makeStyles(theme => ({
  root: {
    '& label.Mui-focused': {
      color: theme.palette.primary.light,
    },
    '& .MuiInput-underline:before': {
      borderBottomColor: theme.palette.divider,
    },
    '& .MuiInput-underline:after': {
      borderBottomColor: theme.palette.primary.light,
    },
  },
  inputRoot: {
    fontSize: 12,
  },
}));

const fullNameSectionStyle = makeStyles(theme => ({
  fullName: {
    fontWeight: 600,
    fontSize: '1.1rem',
    maxWidth: '139px',
  },
  svgIcon: {
    color: theme.b2chatgray[700],
  },
}));

const ContactForm = ({
  avatarUrl,
  contactForm,
  countriesDataSet,
  mobileNumberDisabled,
  customFieldsWithError,
  arrSelCountryProvinces,
  onChangeContactFormField,
  handleSetCountryProvinces,
  bsuid,
  whatsappUsername,
  phoneRequestStatus,
  hasWaUserId,
  onRequestNumber,
  savedMobileNumber,
  intl,
}) => {
  const [urlImage, setUrlImage] = useState(null);
  const [loadingImage, setLoadingImage] = useState(false);
  const [editingFullName, setEditingFullName] = useState(false);

  const classes = comboBoxStyles();
  const fullNameStyle = fullNameSectionStyle();

  const countriesLength = countriesDataSet.length;
  const provincesLength = arrSelCountryProvinces.length;

  if (countriesLength > 0 && countriesDataSet[countriesLength - 1].isoCode) {
    countriesDataSet.push({ isoCode: null, name: '' });
  }
  if (provincesLength > 0 && arrSelCountryProvinces[provincesLength - 1].id) {
    arrSelCountryProvinces.push({ id: null, name: '' });
  }

  const handleContactFieldChanged = (fieldName, newVal) => {
    onChangeContactFormField(fieldName, newVal);
  };

  const loadProvincesAutoComplete = curCountry => {
    if (curCountry && !StringUtils.isBlank(curCountry.isoCode)) {
      MasterDataProvider.getCountryProvincesArray(
        curCountry.isoCode,
        arrProvinces => {
          handleSetCountryProvinces(arrProvinces);
        }
      );
    }
  };

  const handleCountrySelected = (selItem, selIndex) => {
    if (selIndex >= 0) {
      loadProvincesAutoComplete(selItem);
      onChangeContactFormField(COUNTRY, selItem);
    }
    onChangeContactFormField(PROVINCE, { id: null, name: '' });
  };

  useEffect(() => {
    if (avatarUrl && avatarUrl !== urlImage) {
      setLoadingImage(true);
      const img = new window.Image();
      img.src = avatarUrl;
      img.onload = () => {
        setUrlImage(avatarUrl);
        setLoadingImage(false);
      };
      img.onerror = () => {
        setUrlImage(null);
        setLoadingImage(false);
      };
    }

    if (!avatarUrl) setUrlImage(null);
  }, [avatarUrl, urlImage]);

  const onChangeCountry = (_, eventValue) => {
    const value = eventValue || {
      isoCode: null,
      name: '',
    };

    onChangeContactFormField(COUNTRY, value);
    handleCountrySelected(value, countriesDataSet.indexOf(value));
  };

  const onChangeProvince = (_, eventValue) => {
    const value = eventValue || {
      isoCode: null,
      name: '',
    };
    onChangeContactFormField(PROVINCE, value);
  };

  const idField = contactForm.fields[ID];
  const fullNameField = contactForm.fields[FULL_NAME];
  const idNumberField = contactForm.fields[ID_NUMBER];
  const emailField = contactForm.fields[EMAIL];
  const phoneNumberField = contactForm.fields[PHONE_NUMBER];
  const mobileNumberField = contactForm.fields[MOBILE_NUMBER];
  const addressField = contactForm.fields[ADDRESS];
  const companyField = contactForm.fields[COMPANY] || '';
  const countryField = contactForm.fields[COUNTRY] || {
    value: { isoCode: null, name: '' },
  };
  const provinceField = contactForm.fields[PROVINCE] || {
    value: { id: null, name: '' },
  };
  const cityNameField = contactForm.fields[CITY_NAME] || { value: '' };

  const loadingImageView = (
    <Avatar style={{ zIndex: 20, color: '#4e3aff' }}>
      <CircularProgress color="inherit" size={20} />
    </Avatar>
  );
  const avatarImageView = (
    <Avatar src={urlImage} style={{ zIndex: 20, color: '#4e3aff' }} />
  );
  const CustomAvatarView = <CustomAvatar username={fullNameField.value} />;

  return (
    <form style={{ width: '100%' }}>
      {customFieldsWithError && (
        <div style={{ paddingBottom: '8px' }}>
          <span className="error-label">
            <FormattedMessage id="ContactEditorForm.customFieldsErrorNotification" />
          </span>
        </div>
      )}
      <div>
        <input type="hidden" name="id" value={idField.value} />
        <Box
          display="flex"
          alignItems="center"
          flexDirection="row"
          style={{ gap: '8px' }}
          mb={1.5}
        >
          <div className="AvatarContainer">
            {loadingImage && loadingImageView}
            {!loadingImage && urlImage && avatarImageView}
            {!urlImage && !loadingImage && CustomAvatarView}
          </div>
          {editingFullName && (
            <InputHelperText error={fullNameField.error}>
              <TextField
                name="fullName"
                className="InputText"
                value={fullNameField.value}
                inputProps={{
                  maxLength: 200,
                }}
                onChange={({ target }) => {
                  handleContactFieldChanged(FULL_NAME, target.value);
                }}
                onKeyDown={event => {
                  if (event.key === 'Enter') setEditingFullName(false);
                }}
                style={{ marginLeft: '13px' }}
                autoComplete="off"
              />
            </InputHelperText>
          )}
          {!editingFullName && (
            <Box
              display="flex"
              justifyContent="space-between"
              alignItems="center"
              width="100%"
              height={45}
            >
              <Typography
                className={`${fullNameStyle.fullName} ellipsis-three-lines`}
              >
                {fullNameField.value}
              </Typography>
              <IconButton
                size="small"
                color="inherit"
                className={fullNameStyle.svgIcon}
                onClick={() => setEditingFullName(true)}
              >
                {EditIcon}
              </IconButton>
            </Box>
          )}
        </Box>

        <ReadOnlyContactFields
          bsuid={bsuid}
          whatsappUsername={whatsappUsername}
        />

        <InputHelperText error={idNumberField.error}>
          <TextField
            name="identificationNumber"
            className="InputText"
            value={idNumberField.value}
            inputProps={{
              maxLength: 50,
            }}
            label={<FormattedMessage id="ContactForm.id" />}
            onChange={({ target }) => {
              handleContactFieldChanged(ID_NUMBER, target.value);
            }}
            autoComplete="off"
          />
        </InputHelperText>

        <InputHelperText error={emailField.error}>
          <TextField
            name="email"
            type="email"
            className="InputText"
            value={emailField.value}
            inputProps={{
              maxLength: 200,
            }}
            label={<FormattedMessage id="ContactForm.email" />}
            onChange={({ target }) => {
              handleContactFieldChanged(EMAIL, target.value);
            }}
            autoComplete="off"
          />
        </InputHelperText>

        <InputHelperText error={phoneNumberField.error}>
          <E164PhoneField
            name="phoneNumber"
            className="InputText"
            value={phoneNumberField.value}
            inputProps={{
              maxLength: 25,
            }}
            label={<FormattedMessage id="ContactForm.phoneNumber" />}
            onChange={({ target }) => {
              handleContactFieldChanged(PHONE_NUMBER, target.value);
            }}
            autoComplete="off"
          />
        </InputHelperText>

        <MobileNumberField
          value={mobileNumberField.value}
          savedValue={savedMobileNumber}
          error={mobileNumberField.error}
          disabled={mobileNumberDisabled}
          phoneRequestStatus={phoneRequestStatus}
          hasWaUserId={hasWaUserId}
          onChange={val => handleContactFieldChanged(MOBILE_NUMBER, val)}
          onRequestNumber={onRequestNumber}
        />

        <InputHelperText error={addressField.error}>
          <TextField
            name="address"
            className="InputText"
            value={addressField.value}
            inputProps={{
              maxLength: 300,
            }}
            label={<FormattedMessage id="ContactForm.address" />}
            onChange={({ target }) => {
              handleContactFieldChanged(ADDRESS, target.value);
            }}
            autoComplete="off"
          />
        </InputHelperText>

        <InputHelperText error={companyField.error}>
          <TextField
            name="company"
            className="InputText"
            value={companyField.value}
            inputProps={{
              maxLength: 100,
            }}
            label={<FormattedMessage id="ContactForm.company" />}
            onChange={({ target }) => {
              handleContactFieldChanged(COMPANY, target.value);
            }}
            autoComplete="off"
          />
        </InputHelperText>

        <Autocomplete
          name="country"
          className="dropDownSelect"
          classes={classes}
          options={countriesDataSet}
          getOptionLabel={option => {
            const countryName =
              option?.name ||
              countriesDataSet.find(f => f.isoCode === option.isoCode)?.name;
            return countryName || '';
          }}
          value={countryField.value}
          getOptionSelected={(option, value) =>
            option.isoCode === value.isoCode
          }
          renderInput={params => (
            <TextField
              {...params}
              placeholder={intl.formatMessage({ id: 'ContactForm.country' })}
            />
          )}
          onChange={onChangeCountry}
          autoComplete="off"
        />

        <Autocomplete
          name="province"
          className="dropDownSelect"
          classes={classes}
          options={arrSelCountryProvinces}
          getOptionLabel={option => {
            const provinceName =
              option?.name ||
              arrSelCountryProvinces.find(f => f.id === option.id)?.name;
            return provinceName || '';
          }}
          value={provinceField.value}
          getOptionSelected={(option, value) => option.id === value.id}
          renderInput={params => (
            <TextField
              {...params}
              placeholder={intl.formatMessage({ id: 'ContactForm.province' })}
              autoComplete="off"
            />
          )}
          onChange={onChangeProvince}
        />

        <InputHelperText error={cityNameField.error}>
          <TextField
            name="cityName"
            className="InputText"
            value={cityNameField.value}
            inputProps={{
              maxLength: 100,
            }}
            label={<FormattedMessage id="ContactForm.cityName" />}
            onChange={({ target }) => {
              handleContactFieldChanged(CITY_NAME, target.value);
            }}
            autoComplete="off"
          />
        </InputHelperText>
      </div>
    </form>
  );
};

export default injectIntl(ContactForm);
